/* №6 «Нэвтрэх» — дизайны login.jsx визуал + бодит TseTsu auth (нууц үг / SMS OTP) */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { auth, type LoginResponse } from '@/shared/auth/storage';
import { buildScopedPath, publicRoutes } from '@/app/routes';
import { useMediaQuery } from '@/shared/useMediaQuery';
import {
  IconArrowRight, IconCheck, IconDroplets, IconEye, IconEyeOff, IconLoader, IconLock, IconPhone,
} from '@/shared/icons';

const BRAND_POINTS = ['Хурдан дараалал, хүлээлгүй', 'И-баримт автоматаар', 'Харилцагчийн урамшуулал'];

function BrandPanel({ compact }: { compact?: boolean }) {
  return (
    <div className={'relative overflow-hidden text-white flex flex-col justify-between bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] ' + (compact ? 'px-6 py-7' : 'p-10')}>
      {/* усны товгор хээ */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full border-[24px] border-white/40"></div>
        <div className="absolute right-10 bottom-10 w-40 h-40 rounded-full border-[16px] border-white/30"></div>
        <div className="absolute left-8 bottom-24 w-20 h-20 rounded-full bg-white/20"></div>
      </div>

      <div className="relative flex items-center gap-3">
        <div className={'rounded-[14px] bg-white/15 backdrop-blur flex items-center justify-center ' + (compact ? 'w-10 h-10' : 'w-12 h-12')}>
          <IconDroplets size={compact ? 22 : 26} strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <div className={'font-extrabold tracking-tight ' + (compact ? 'text-[18px]' : 'text-[22px]')}>Carwash</div>
          <div className="text-[12px] text-cyan-50/80 font-medium">tsetsu.mn</div>
        </div>
      </div>

      {!compact && (
        <div className="relative flex flex-col gap-5">
          <div>
            <div className="text-[30px] font-extrabold leading-[1.15] tracking-tight">Машин угаалгаа<br />хялбар удирд</div>
            <div className="text-[15px] text-cyan-50/85 mt-3 leading-relaxed">Дараалал, төлбөр, и-баримт, харилцагчийн урамшуулал — нэг дороос.</div>
          </div>
          <div className="flex flex-col gap-2.5">
            {BRAND_POINTS.map((p) => (
              <div key={p} className="flex items-center gap-2.5 text-[14px] text-cyan-50">
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0"><IconCheck size={12} strokeWidth={3} /></span>
                {p}
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && <div className="relative text-[12px] text-cyan-50/60">© 2026 tsetsu.mn</div>}
    </div>
  );
}

function LoginForm() {
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpStarted, setOtpStarted] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [destNumber, setDestNumber] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const pollTimer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => () => clearInterval(pollTimer.current), []);

  const digits = phone.replace(/\D/g, '');
  const phoneOk = digits.length === 8;
  const canSubmit = phoneOk && pass.length >= 4 && !busy;

  const fmtPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.length > 4 ? d.slice(0, 4) + ' ' + d.slice(4) : d;
  };

  const finishLogin = (res: LoginResponse) => {
    auth.saveLogin(res);
    if (res.DefaultTenantId && res.DefaultCompanyId) {
      nav(buildScopedPath('queue', { tenantId: res.DefaultTenantId, companyId: res.DefaultCompanyId }), { replace: true });
    } else {
      nav(publicRoutes.selectScope, { replace: true });
    }
  };

  const passwordLogin = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      finishLogin(await authApi.passwordLogin(digits, pass));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Нэвтрэхэд алдаа гарлаа.');
      setBusy(false);
    }
  };

  const otpLogin = async () => {
    if (!phoneOk || busy) return;
    setBusy(true);
    setError(null);
    try {
      const start = await authApi.startOtp(digits);
      // verify.mn inbound SMS: хэрэглэгч доорх кодыг тусгай дугаар руу SMS-ээр илгээнэ.
      const instr = start.DisplayInstruction ?? '';
      const code = (instr.match(/\b(\d{4})\b/) ?? [])[1] ?? '';
      const dest = (instr.match(/\b(\d{5,7})\b/) ?? [])[1] ?? '';
      setSmsCode(code);
      setDestNumber(dest);
      setOtpHint(code ? '' : instr || 'SMS-ээр ирсэн зааврын дагуу баталгаажуулна уу…');
      setOtpStarted(true);
      pollTimer.current = setInterval(async () => {
        try {
          const st = await authApi.pollOtp(start.SessionId);
          if (st.SessionStatus === 'VERIFIED') {
            clearInterval(pollTimer.current);
            finishLogin(await authApi.phoneLogin(digits, start.SessionId));
          } else if (st.SessionStatus === 'EXPIRED' || st.SessionStatus === 'FAILED') {
            clearInterval(pollTimer.current);
            setOtpStarted(false);
            setError('Баталгаажуулалт амжилтгүй — дахин оролдоно уу.');
            setBusy(false);
          }
        } catch {
          /* poll-ийн түр алдааг дараагийн оролдлого нөхнө */
        }
      }, 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Баталгаажуулалт эхлүүлэхэд алдаа гарлаа.');
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-[360px] flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-[24px] font-extrabold text-slate-900 tracking-tight">Нэвтрэх</h1>
        <p className="text-[14px] text-slate-500">Ажлын бүртгэлээрээ нэвтэрнэ үү</p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Утасны дугаар</span>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"><IconPhone size={17} /></span>
            <input value={phone} onChange={(e) => setPhone(fmtPhone(e.target.value))} inputMode="numeric" placeholder="9911 2233"
              disabled={busy}
              className="w-full h-12 pl-11 pr-4 rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-200 text-[16px] font-semibold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
          </div>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-bold uppercase tracking-wide text-slate-400">Нууц үг</span>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"><IconLock size={17} /></span>
            <input value={pass} onChange={(e) => setPass(e.target.value)} type={show ? 'text' : 'password'} placeholder="••••••"
              disabled={busy}
              onKeyDown={(e) => { if (e.key === 'Enter') void passwordLogin(); }}
              className="w-full h-12 pl-11 pr-11 rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-200 text-[16px] font-semibold text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
            <button onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100">
              {show ? <IconEyeOff size={17} /> : <IconEye size={17} />}
            </button>
          </div>
        </label>
      </div>

      {error && <div className="text-[13px] font-medium text-rose-600 bg-rose-50 ring-1 ring-rose-100 rounded-[10px] px-3.5 py-2.5">{error}</div>}
      {otpStarted && (
        <div className="flex flex-col gap-2.5 text-cyan-800 bg-cyan-50 ring-1 ring-cyan-200 rounded-[12px] px-4 py-3.5">
          <div className="text-[13px] font-medium leading-relaxed">
            Доорх кодыг {destNumber ? <b className="font-bold">{destNumber}</b> : 'тусгай дугаар'} руу SMS-ээр илгээнэ үү.
          </div>
          {smsCode ? (
            <div className="text-center text-[28px] font-extrabold tracking-[0.2em] text-cyan-900 select-all">
              {smsCode}
            </div>
          ) : (
            otpHint && <div className="text-[12px] text-cyan-700/80">{otpHint}</div>
          )}
          <div className="flex items-center gap-2 text-[12px] font-medium text-cyan-700">
            <IconLoader size={14} className="animate-spin shrink-0" />
            Баталгаажуулалтыг хүлээж байна…
          </div>
        </div>
      )}

      <button onClick={() => void passwordLogin()} disabled={!canSubmit}
        className={
          'inline-flex items-center justify-center gap-2 min-h-[52px] rounded-[var(--r)] text-[15px] font-semibold transition active:scale-[0.99] ' +
          (canSubmit
            ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95'
            : 'text-slate-400 bg-slate-100 cursor-not-allowed')
        }>
        {busy && !otpStarted ? 'Нэвтэрч байна…' : 'Нэвтрэх'} <IconArrowRight size={18} strokeWidth={2.5} />
      </button>

      <div className="flex items-center gap-3 text-[12px] text-slate-300">
        <span className="flex-1 h-px bg-slate-200"></span>эсвэл<span className="flex-1 h-px bg-slate-200"></span>
      </div>

      <button onClick={() => void otpLogin()} disabled={!phoneOk || busy}
        className={'text-[14px] font-semibold text-center transition ' +
          (phoneOk && !busy ? 'text-[var(--brand1)] hover:underline' : 'text-slate-300 cursor-not-allowed')}>
        SMS-ээр нэвтрэх
      </button>
    </div>
  );
}

export function LoginPage() {
  const mobile = useMediaQuery('(max-width: 767px)');
  if (mobile) {
    return (
      <div className="h-screen flex flex-col bg-white overflow-y-auto font-sans">
        <BrandPanel compact />
        <div className="flex-1 flex items-start justify-center px-6 py-8">
          <LoginForm />
        </div>
      </div>
    );
  }
  return (
    <div className="h-screen grid grid-cols-2 bg-white font-sans">
      <BrandPanel />
      <div className="flex items-center justify-center p-10">
        <LoginForm />
      </div>
    </div>
  );
}
